import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Mail, Phone, ArrowRight, ChevronDown, Plus, X, Check, ArrowUpRight
} from 'lucide-react';

// ─── SHARED PRIMITIVES ─────────────────────────────────────────

const FadeIn = ({ children, className = "", delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.7, delay, ease: [0.25, 0.1, 0.25, 1] }}
    viewport={{ once: true, margin: "-50px" }}
    className={className}
  >
    {children}
  </motion.div>
);

// ─── COUNTDOWN TIMER ───────────────────────────────────────────

const CountdownTimer = ({ targetDate }) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const distance = targetDate - now;
      if (distance > 0) {
        setTimeLeft({
          days: Math.floor(distance / (1000 * 60 * 60 * 24)),
          hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((distance % (1000 * 60)) / 1000)
        });
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [targetDate]);

  return (
    <div className="flex gap-3 sm:gap-4 justify-center">
      {Object.entries(timeLeft).map(([unit, value]) => (
        <div key={unit} className="text-center">
          <div className="bg-white/10 backdrop-blur-sm border border-amber-300/20 rounded-lg px-3 py-2 sm:px-5 sm:py-3 min-w-[56px] sm:min-w-[72px]">
            <div className="text-xl sm:text-3xl font-serif font-light text-white tabular-nums">
              {String(value).padStart(2, '0')}
            </div>
            <div className="text-amber-200/60 text-[10px] sm:text-xs uppercase tracking-widest mt-0.5">
              {unit}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── ACCORDION ITEM ─────────────────────────────────────────────

const AccordionItem = ({ item, isOpen, onToggle }) => (
  <div className="border-b border-stone-200 last:border-0">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between py-5 sm:py-6 text-left group min-h-[44px]"
    >
      <div className="flex items-center gap-4 pr-4">
        <span className="text-lg sm:text-xl font-serif text-stone-800 group-hover:text-amber-700 transition-colors">
          {item.title}
        </span>
        {item.subtitle && (
          <span className="text-sm text-stone-400 hidden sm:inline">{item.subtitle}</span>
        )}
      </div>
      <motion.div
        animate={{ rotate: isOpen ? 45 : 0 }}
        transition={{ duration: 0.2 }}
        className="text-amber-600 flex-shrink-0"
      >
        <Plus className="w-5 h-5" />
      </motion.div>
    </button>
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          className="overflow-hidden"
        >
          <div className="pb-6 pr-8">
            <p className="text-stone-600 leading-relaxed mb-4 text-sm sm:text-base">
              {item.description}
            </p>
            {item.features && (
              <ul className="space-y-2">
                {item.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-stone-500">
                    <Check className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

// ─── PHOTO GALLERY ──────────────────────────────────────────────

const PhotoGallery = () => {
  const images = [
    "https://i.ibb.co/3mQwhwH6/Whats-App-Image-2025-10-13-at-13-36-29.jpg",
    "https://i.ibb.co/S7wG7N11/Whats-App-Image-2025-10-13-at-13-34-53.jpg",
    "https://i.ibb.co/gLXGWkWj/Whats-App-Image-2025-10-13-at-13-34-53-1.jpg",
    "https://i.ibb.co/1fFSbqTw/Whats-App-Image-2025-10-13-at-13-34-50.jpg",
    "https://i.ibb.co/V0WF4vvT/Whats-App-Image-2025-10-13-at-13-34-50-1.jpg",
    "https://i.ibb.co/gKZGrqD/Whats-App-Image-2025-10-13-at-13-34-49.jpg",
    "https://i.ibb.co/wFrW01HG/Whats-App-Image-2025-10-13-at-13-34-49-1.jpg",
    "https://i.ibb.co/PsT5787r/Whats-App-Image-2025-10-13-at-13-34-44.jpg",
    "https://i.ibb.co/Fq8CLDBX/Whats-App-Image-2025-10-13-at-13-41-02.jpg",
    "https://i.ibb.co/SDGprL3c/Whats-App-Image-2025-10-13-at-13-41-02-2.jpg",
    "https://i.ibb.co/GfxvXC07/Whats-App-Image-2025-10-13-at-13-41-02-1.jpg",
    "https://i.ibb.co/q3LJXrty/Whats-App-Image-2025-10-13-at-13-41-01.jpg",
    "https://i.ibb.co/3YFS8TVV/Whats-App-Image-2025-10-13-at-13-41-01-4.jpg",
    "https://i.ibb.co/twbnT9Cn/Whats-App-Image-2025-10-13-at-13-41-01-3.jpg",
    "https://i.ibb.co/Kz8BgWXc/Whats-App-Image-2025-10-13-at-13-41-01-2.jpg",
    "https://i.ibb.co/Mx3Hxf5g/Whats-App-Image-2025-10-13-at-13-41-01-1.jpg",
    "https://i.ibb.co/SDXwvCfn/Whats-App-Image-2025-10-13-at-13-40-59.jpg",
    "https://i.ibb.co/RTY2cChW/Whats-App-Image-2025-10-13-at-13-40-58.jpg",
  ];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const nextImage = () => setCurrentIndex((prev) => (prev + 1) % images.length);
  const prevImage = () => setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);

  return (
    <section className="py-20 sm:py-28 bg-cream">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <FadeIn className="text-center mb-12 sm:mb-16">
          <p className="text-xs sm:text-sm uppercase tracking-[0.2em] text-amber-700 mb-4">Gallery</p>
          <h2 className="text-3xl sm:text-5xl font-serif font-light text-stone-800">
            Moments from Past Retreats
          </h2>
        </FadeIn>

        <FadeIn delay={0.1}>
          <div className="relative rounded-xl overflow-hidden bg-stone-100 aspect-[16/10] sm:aspect-[2/1]">
            <AnimatePresence mode="wait">
              <motion.img
                key={currentIndex}
                src={images[currentIndex]}
                alt={`Retreat moment ${currentIndex + 1}`}
                className="w-full h-full object-cover cursor-pointer"
                onClick={() => setIsFullscreen(true)}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
              />
            </AnimatePresence>

            <button
              onClick={prevImage}
              className="absolute left-3 sm:left-5 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2.5 rounded-full shadow-sm transition-all min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label="Previous image"
            >
              <ArrowRight className="w-4 h-4 text-stone-700 rotate-180" />
            </button>
            <button
              onClick={nextImage}
              className="absolute right-3 sm:right-5 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2.5 rounded-full shadow-sm transition-all min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label="Next image"
            >
              <ArrowRight className="w-4 h-4 text-stone-700" />
            </button>

            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-stone-900/60 backdrop-blur-sm text-white/90 px-3 py-1.5 rounded-full text-xs tracking-wide">
              {currentIndex + 1} / {images.length}
            </div>
          </div>

          {/* Thumbnail strip */}
          <div className="mt-4 overflow-x-auto">
            <div className="flex gap-2 pb-1">
              {images.map((img, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`relative flex-shrink-0 w-16 h-12 sm:w-20 sm:h-14 rounded-md overflow-hidden transition-all ${
                    index === currentIndex
                      ? 'ring-2 ring-amber-600 opacity-100'
                      : 'opacity-50 hover:opacity-80'
                  }`}
                  aria-label={`Go to image ${index + 1}`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        </FadeIn>
      </div>

      {/* Fullscreen overlay */}
      <AnimatePresence>
        {isFullscreen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
            onClick={() => setIsFullscreen(false)}
          >
            <button
              onClick={() => setIsFullscreen(false)}
              className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 p-3 rounded-full transition-all min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label="Close fullscreen"
            >
              <X className="w-5 h-5 text-white" />
            </button>
            <img
              src={images[currentIndex]}
              alt=""
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={(e) => { e.stopPropagation(); prevImage(); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 p-3 rounded-full min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label="Previous image"
            >
              <ArrowRight className="w-5 h-5 text-white rotate-180" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); nextImage(); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 p-3 rounded-full min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label="Next image"
            >
              <ArrowRight className="w-5 h-5 text-white" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

// ─── TESTIMONIAL CARD ───────────────────────────────────────────

const TestimonialCard = ({ name, text, delay = 0 }) => (
  <FadeIn delay={delay} className="bg-white rounded-xl p-6 sm:p-8 shadow-sm border border-stone-100">
    <blockquote className="text-stone-600 leading-relaxed text-sm sm:text-base mb-6 italic">
      "{text}"
    </blockquote>
    <div className="flex items-center gap-3">
      <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
        <span className="text-amber-700 text-sm font-medium">{name[0]}</span>
      </div>
      <span className="text-stone-800 font-medium text-sm">{name}</span>
    </div>
  </FadeIn>
);

// ─── DAY SCHEDULE DATA ──────────────────────────────────────────

const days = [
  {
    label: "Day 1",
    date: "Mar 22",
    fullLabel: "Day 1 — Sunday",
    theme: "Arrival & Opening",
    schedule: [
      { time: "15:00–18:00", activity: "Arrivals & settling in" },
      { time: "18:00–19:00", activity: "Welcome circle & introductions" },
      { time: "19:00–20:00", activity: "Opening dinner together" },
      { time: "20:00–21:30", activity: "Evening opening ceremony & zikr" }
    ]
  },
  {
    label: "Day 2",
    date: "Mar 23",
    fullLabel: "Day 2 — Monday",
    theme: "Awakening",
    schedule: [
      { time: "07:00–08:00", activity: "Morning muraqaba meditation" },
      { time: "08:00–09:00", activity: "Breakfast" },
      { time: "09:30–12:00", activity: "Sufi yoga & sacred movement" },
      { time: "12:00–13:30", activity: "Lunch & rest" },
      { time: "14:00–16:00", activity: "Teaching: The Sufi path of the heart" },
      { time: "16:30–18:00", activity: "Guided nature walk & reflection" },
      { time: "19:00–20:00", activity: "Dinner" },
      { time: "20:30–22:00", activity: "Live Sufi music & poetry" }
    ]
  },
  {
    label: "Day 3",
    date: "Mar 24",
    fullLabel: "Day 3 — Tuesday",
    theme: "Deepening",
    schedule: [
      { time: "07:00–08:00", activity: "Morning practices & breathwork" },
      { time: "08:00–09:00", activity: "Breakfast" },
      { time: "09:30–12:00", activity: "Art therapy & creative expression" },
      { time: "12:00–13:30", activity: "Lunch & rest" },
      { time: "14:00–16:00", activity: "Sufi cooking workshop" },
      { time: "16:30–18:00", activity: "Group sharing circles" },
      { time: "19:00–20:00", activity: "Dinner" },
      { time: "20:30–22:00", activity: "Sama performance & hadra" }
    ]
  },
  {
    label: "Day 4",
    date: "Mar 25",
    fullLabel: "Day 4 — Wednesday",
    theme: "Stillness",
    schedule: [
      { time: "07:00–08:00", activity: "Sunrise meditation" },
      { time: "08:00–09:00", activity: "Breakfast" },
      { time: "09:30–12:30", activity: "Equestrian therapy & nature immersion" },
      { time: "12:30–14:00", activity: "Lunch & rest" },
      { time: "14:30–17:00", activity: "Deep teaching: Love & the Sufi way" },
      { time: "17:00–18:30", activity: "Journaling & personal reflection" },
      { time: "19:00–20:00", activity: "Dinner" },
      { time: "20:30–22:00", activity: "Sacred storytelling & poetry night" }
    ]
  },
  {
    label: "Day 5",
    date: "Mar 26",
    fullLabel: "Day 5 — Thursday",
    theme: "Connection",
    schedule: [
      { time: "07:00–08:00", activity: "Morning zikr" },
      { time: "08:00–09:00", activity: "Breakfast" },
      { time: "09:30–12:00", activity: "Photography walk & mindful seeing" },
      { time: "12:00–13:30", activity: "Lunch & rest" },
      { time: "14:00–16:00", activity: "Advanced Sufi practices" },
      { time: "16:30–18:00", activity: "Community service & giving" },
      { time: "19:00–20:00", activity: "Special celebration dinner" },
      { time: "20:30–22:30", activity: "Full hadra ceremony" }
    ]
  },
  {
    label: "Day 6",
    date: "Mar 27",
    fullLabel: "Day 6 — Friday",
    theme: "Integration",
    schedule: [
      { time: "07:00–08:00", activity: "Morning practices" },
      { time: "08:00–09:00", activity: "Breakfast" },
      { time: "09:30–12:00", activity: "Integration workshop" },
      { time: "12:00–13:30", activity: "Lunch" },
      { time: "14:00–16:00", activity: "Personal practice time" },
      { time: "16:30–18:00", activity: "Final sharing circle" },
      { time: "19:00–20:00", activity: "Farewell dinner" },
      { time: "20:30–22:00", activity: "Closing ceremony & blessings" }
    ]
  },
  {
    label: "Day 7",
    date: "Mar 28",
    fullLabel: "Day 7 — Saturday",
    theme: "Departure",
    schedule: [
      { time: "07:00–08:00", activity: "Final morning meditation" },
      { time: "08:00–09:00", activity: "Farewell breakfast together" },
      { time: "09:00–11:00", activity: "Check-out & farewells" },
      { time: "11:00", activity: "Departures with transformed hearts" }
    ]
  }
];

// ─── DAY SCHEDULE SECTION ────────────────────────────────────────

const DayScheduleSection = () => {
  const [activeDay, setActiveDay] = useState(0);

  return (
    <section className="py-20 sm:py-28 bg-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <FadeIn className="text-center mb-12 sm:mb-16">
          <p className="text-xs sm:text-sm uppercase tracking-[0.2em] text-amber-700 mb-4">The Journey</p>
          <h2 className="text-3xl sm:text-5xl font-serif font-light text-stone-800 mb-4">
            Your 7-Day Programme
          </h2>
          <p className="text-base sm:text-lg text-stone-500 max-w-2xl mx-auto leading-relaxed">
            Each day unfolds with intention. Select a day below to explore what awaits.
          </p>
        </FadeIn>

        {/* Day tab strip */}
        <FadeIn delay={0.1}>
          <div className="overflow-x-auto mb-8 sm:mb-10">
            <div className="flex gap-2 pb-1 min-w-max sm:min-w-0 sm:flex-wrap sm:justify-center">
              {days.map((day, index) => (
                <button
                  key={index}
                  onClick={() => setActiveDay(index)}
                  className={`flex-shrink-0 px-4 py-2.5 rounded-full text-sm font-medium transition-all min-h-[44px] ${
                    activeDay === index
                      ? 'bg-stone-800 text-white'
                      : 'bg-sand text-stone-600 hover:bg-stone-200'
                  }`}
                >
                  <span className="hidden sm:inline">{day.fullLabel.split('—')[0].trim()}</span>
                  <span className="sm:hidden">{day.date}</span>
                </button>
              ))}
            </div>
          </div>
        </FadeIn>

        {/* Day content panel */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeDay}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.35, ease: "easeInOut" }}
          >
            <div className="bg-sand rounded-2xl p-6 sm:p-10">
              {/* Day header */}
              <div className="border-b border-stone-200 pb-6 mb-6">
                <div className="flex items-baseline gap-4 flex-wrap">
                  <h3 className="text-2xl sm:text-3xl font-serif text-stone-800">
                    {days[activeDay].fullLabel}
                  </h3>
                  <span className="text-amber-700 font-medium text-sm uppercase tracking-widest">
                    {days[activeDay].theme}
                  </span>
                </div>
              </div>

              {/* Schedule items */}
              <div className="space-y-3">
                {days[activeDay].schedule.map((item, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: idx * 0.04 }}
                    className="flex items-start gap-4 bg-white rounded-lg px-5 py-4"
                  >
                    <span className="text-amber-700 text-xs font-medium tracking-wide uppercase whitespace-nowrap pt-0.5 min-w-[90px] sm:min-w-[110px] flex-shrink-0">
                      {item.time}
                    </span>
                    <span className="text-stone-700 text-sm sm:text-base leading-snug">
                      {item.activity}
                    </span>
                  </motion.div>
                ))}
              </div>

              {/* Prev / Next navigation */}
              <div className="flex justify-between items-center mt-8 pt-6 border-t border-stone-200">
                <button
                  onClick={() => setActiveDay((prev) => Math.max(0, prev - 1))}
                  disabled={activeDay === 0}
                  className="flex items-center gap-2 text-stone-500 text-sm disabled:opacity-30 hover:text-stone-800 transition-colors min-h-[44px] px-2"
                >
                  <ArrowRight className="w-4 h-4 rotate-180" />
                  <span className="hidden sm:inline">Previous day</span>
                </button>
                <span className="text-stone-400 text-xs tracking-wide">
                  {activeDay + 1} of {days.length}
                </span>
                <button
                  onClick={() => setActiveDay((prev) => Math.min(days.length - 1, prev + 1))}
                  disabled={activeDay === days.length - 1}
                  className="flex items-center gap-2 text-stone-500 text-sm disabled:opacity-30 hover:text-stone-800 transition-colors min-h-[44px] px-2"
                >
                  <span className="hidden sm:inline">Next day</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

// ─── MAIN COMPONENT ─────────────────────────────────────────────

const MarchRetreat = () => {
  const retreatDate = new Date('2027-03-22T00:00:00').getTime();
  const [openAccordion, setOpenAccordion] = useState(null);

  const activities = [
    {
      title: "Daily Sufi Meditations & Zikr",
      description: "Begin each morning with guided muraqaba and sacred remembrance. These daily practices of zikr and contemplation still the mind, open the heart, and set the tone for each transformative day."
    },
    {
      title: "Creative Expression & Art Therapy",
      description: "Sufi art therapy sessions guide you to express the inexpressible — through drawing, painting, and sacred writing. Centuries-old practices for accessing deeper states of consciousness."
    },
    {
      title: "Sama & Sufi Music Performances",
      description: "Experience the mystical tradition of sama — sacred listening — with live Sufi music performances. This haunting music opens the heart to divine presence and has been central to the Sufi path for centuries."
    },
    {
      title: "Nature Immersion & Equestrian Therapy",
      description: "Guided immersion in the natural world and unique equestrian therapy sessions. Horses are powerful mirrors of our emotional state, offering profound insights for the sincere seeker."
    },
    {
      title: "Sufi Cuisine & Cooking Workshops",
      description: "Experience the kitchen as a place of meditation. Prepare food with intention and love, and discover how nourishing others is one of the highest expressions of the Sufi path."
    },
    {
      title: "Poetry Nights & Sacred Storytelling",
      description: "Evening gatherings around the sacred poetry of Rumi, Hafez, and other masters. These words carry a living transmission — guiding seekers to the divine for centuries."
    }
  ];

  const includedItems = [
    {
      title: "7-Night Accommodation",
      subtitle: "Alaya Retreat Center",
      description: "Full-board accommodation at the serene Alaya Retreat Center in Barcelona for the entire 7-day journey. Private and shared room options available.",
      features: ["7 nights at retreat center", "Beautiful natural surroundings", "Quiet garden and reflection spaces", "Private and shared room options"]
    },
    {
      title: "All Meals",
      subtitle: "Nourishing vegetarian cuisine",
      description: "Every meal for the full 7 days, thoughtfully prepared to support your spiritual journey. Includes the hands-on Sufi cooking workshop.",
      features: ["All meals for 7 days", "Fresh vegetarian cuisine", "Sufi cooking workshop included", "Mindful eating practices"]
    },
    {
      title: "Expert Guidance",
      subtitle: "Authentic Sufi practitioners",
      description: "Learn directly from Danish Suhail and Abd al-Wadud Sabaté — respected practitioners carrying centuries of unbroken Sufi wisdom.",
      features: ["Two authentic practitioners", "Decades of combined experience", "Unbroken lineage", "Personal guidance and support"]
    },
    {
      title: "Sacred Practices",
      subtitle: "Complete 7-day programme",
      description: "The full programme: daily muraqaba, Sufi yoga, sama, hadra, equestrian therapy, art therapy, poetry nights, nature walks, and integration workshops.",
      features: ["Daily meditation & zikr", "Sufi yoga & movement", "Live Sufi music & sama", "Equestrian therapy session", "Art therapy workshops", "Integration & closing ceremony"]
    },
    {
      title: "Community",
      subtitle: "A carefully selected group",
      description: "Share this journey with a small, intimate group of sincere seekers. Deep connection forged through collective practice, shared meals, and open-hearted presence.",
      features: ["Small intimate group size", "Group sharing circles", "Collective practices", "Supportive community atmosphere"]
    },
    {
      title: "Integration",
      subtitle: "Lasting tools for home",
      description: "Structured integration workshop, daily journaling prompts, and ongoing support to carry the transformation into your everyday life.",
      features: ["Integration workshop", "Take-home practice guides", "Journaling prompts", "Ongoing community access"]
    }
  ];

  return (
    <div className="font-sans antialiased bg-cream text-stone-800 overflow-x-hidden">

      {/* ─── HERO ──────────────────────────────────────────────── */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <img
            src="https://static.wixstatic.com/media/11062b_0bf57831f109491aba0974490795c48d~mv2.jpeg/v1/fill/w_1074,h_1058,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/11062b_0bf57831f109491aba0974490795c48d~mv2.jpeg"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-stone-950/70 via-stone-900/65 to-amber-950/70" />
        </div>

        <div className="relative z-10 text-center px-4 sm:px-6 max-w-4xl mx-auto">
          {/* Logo */}
          <motion.img
            src="/beshnologo.png"
            alt="Beshno Retreats"
            className="h-10 sm:h-16 w-auto mx-auto mb-10 sm:mb-14 brightness-0 invert opacity-90"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.9 }}
            transition={{ duration: 1.2, delay: 0.2 }}
          />

          {/* Label */}
          <motion.p
            className="text-[11px] sm:text-xs uppercase tracking-[0.3em] text-amber-300/80 mb-5 sm:mb-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.5 }}
          >
            7-Day Immersive Retreat &nbsp;&middot;&nbsp; Barcelona
          </motion.p>

          <motion.h1
            className="text-4xl sm:text-6xl md:text-8xl font-serif font-light text-white mb-4 sm:mb-6 leading-[1.05] tracking-tight"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.6 }}
          >
            Whispers of the Heart
          </motion.h1>

          <motion.p
            className="text-lg sm:text-2xl md:text-3xl font-serif font-light text-white/75 mb-6 sm:mb-8 italic"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.9 }}
          >
            A 7-day Sufi retreat of inner stillness, sacred connection &amp; soulful renewal
          </motion.p>

          {/* Dates & location */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.1 }}
            className="mb-8 sm:mb-10"
          >
            <div className="text-white text-base sm:text-xl font-serif mb-1">
              March 22–28, 2027
            </div>
            <div className="text-white/50 text-sm">Alaya Retreat Center, Barcelona</div>

            {/* Countdown */}
            <div className="mt-7">
              <CountdownTimer targetDate={retreatDate} />
              <p className="text-white/35 text-xs mt-2.5 tracking-wide">Until the retreat begins</p>
            </div>
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.4 }}
            className="space-y-3"
          >
            <a
              href="https://wa.me/34617159090"
              className="inline-flex items-center gap-2 bg-white text-stone-800 px-8 py-3.5 sm:px-10 sm:py-4 rounded-full text-sm sm:text-base font-medium hover:bg-white/90 transition-all shadow-lg"
            >
              Reserve Your Place via WhatsApp
              <ArrowUpRight className="w-4 h-4" />
            </a>
            <p className="text-white/40 text-xs sm:text-sm">
              Limited spaces &nbsp;&middot;&nbsp; Pricing on request
            </p>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-6 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
        >
          <ChevronDown className="w-5 h-5 text-white/30" />
        </motion.div>
      </section>


      {/* ─── INTRO ─────────────────────────────────────────────── */}
      <section className="py-20 sm:py-28 bg-cream">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <FadeIn>
            <p className="text-xs sm:text-sm uppercase tracking-[0.2em] text-amber-700 mb-6">
              The Experience
            </p>
            <h2 className="text-3xl sm:text-5xl font-serif font-light text-stone-800 mb-8 leading-tight">
              Seven days to go deeper than a weekend ever could
            </h2>
            <p className="text-base sm:text-lg text-stone-500 leading-relaxed max-w-2xl mx-auto">
              If your soul is calling for something more immersive — this is it. Seven full days
              to set down the weight of ordinary life, enter the Sufi way with both hands open,
              and return home genuinely changed.
            </p>
          </FadeIn>

          {/* Key details */}
          <FadeIn delay={0.15} className="mt-12 sm:mt-16">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl p-5 sm:p-6 shadow-sm border border-stone-100">
                <div className="text-2xl sm:text-3xl font-serif text-stone-800 mb-1">7 Days</div>
                <div className="text-stone-400 text-sm">Deep immersion</div>
              </div>
              <div className="bg-white rounded-xl p-5 sm:p-6 shadow-sm border border-stone-100">
                <div className="text-2xl sm:text-3xl font-serif text-stone-800 mb-1">Mar 22–28</div>
                <div className="text-stone-400 text-sm">2027 &nbsp;&middot;&nbsp; Barcelona</div>
              </div>
              <div className="bg-white rounded-xl p-5 sm:p-6 shadow-sm border border-stone-100">
                <div className="text-2xl sm:text-3xl font-serif text-stone-800 mb-1">Intimate</div>
                <div className="text-stone-400 text-sm">Small group, personal</div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>


      {/* ─── FULL-WIDTH QUOTE BREAK ─────────────────────────────── */}
      <section className="relative h-[50vh] sm:h-[60vh] overflow-hidden">
        <img
          src="https://static.wixstatic.com/media/7b6fe5_935675ab4bfc4fd0b5e265132dc0c048~mv2.jpg/v1/fill/w_2880,h_1048,al_c,q_90,usm_0.66_1.00_0.01,enc_avif,quality_auto/7b6fe5_935675ab4bfc4fd0b5e265132dc0c048~mv2.jpg"
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-stone-900/50" />
        <div className="absolute inset-0 flex items-center justify-center">
          <FadeIn className="text-center px-6 sm:px-10 max-w-4xl mx-auto">
            <p className="text-[11px] sm:text-xs uppercase tracking-[0.3em] text-amber-300/70 mb-5">
              Rumi
            </p>
            <blockquote className="text-2xl sm:text-4xl md:text-5xl font-serif font-light text-white leading-tight italic">
              "Out beyond ideas of wrongdoing and rightdoing, there is a field. I'll meet you there."
            </blockquote>
          </FadeIn>
        </div>
      </section>


      {/* ─── WHAT AWAITS YOU ────────────────────────────────────── */}
      <section className="py-20 sm:py-28 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <FadeIn className="text-center mb-14 sm:mb-20">
            <p className="text-xs sm:text-sm uppercase tracking-[0.2em] text-amber-700 mb-4">
              Six Pillars
            </p>
            <h2 className="text-3xl sm:text-5xl font-serif font-light text-stone-800">
              What Awaits You
            </h2>
          </FadeIn>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-10 sm:gap-y-14">
            {activities.map((activity, index) => (
              <FadeIn key={index} delay={index * 0.08}>
                <div className="group">
                  <div className="w-10 h-[1px] bg-amber-400 mb-5 group-hover:w-16 transition-all duration-300" />
                  <h3 className="text-xl sm:text-2xl font-serif text-stone-800 mb-3">
                    {activity.title}
                  </h3>
                  <p className="text-stone-500 text-sm sm:text-base leading-relaxed">
                    {activity.description}
                  </p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>


      {/* ─── DAY-BY-DAY SCHEDULE ────────────────────────────────── */}
      <DayScheduleSection />


      {/* ─── PHOTO GALLERY ──────────────────────────────────────── */}
      <PhotoGallery />


      {/* ─── WHY THIS WORKS ─────────────────────────────────────── */}
      <section className="py-20 sm:py-28 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <FadeIn className="text-center mb-14 sm:mb-20">
            <p className="text-xs sm:text-sm uppercase tracking-[0.2em] text-amber-700 mb-4">
              Why This Works
            </p>
            <h2 className="text-3xl sm:text-5xl font-serif font-light text-stone-800 mb-6">
              Seven days changes something in you
            </h2>
            <p className="text-base sm:text-lg text-stone-500 max-w-2xl mx-auto leading-relaxed">
              A weekend opens a door. Seven days lets you walk all the way through it — and
              settle into a new way of being before returning home.
            </p>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
            {[
              {
                title: "Depth a weekend cannot offer",
                desc: "Seven days allows the layers to fall away slowly, naturally — without pressure. By day three, something shifts that simply cannot happen in 48 hours."
              },
              {
                title: "Ancient practices, embodied",
                desc: "800-year-old techniques taught by real practitioners with unbroken lineage. Not theory — lived, embodied, transmissible wisdom."
              },
              {
                title: "A genuine reset",
                desc: "Long enough to fully release the patterns of ordinary life. You go home not just rested, but genuinely reconfigured."
              },
              {
                title: "A community you keep",
                desc: "Seven days forges real bonds. The people you share this journey with often become lifelong companions on the path."
              }
            ].map((benefit, index) => (
              <FadeIn key={index} delay={index * 0.1}>
                <div className="bg-amber-50 rounded-xl p-6 sm:p-8">
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="text-lg font-serif text-stone-800 mb-2">{benefit.title}</h4>
                      <p className="text-stone-500 text-sm leading-relaxed">{benefit.desc}</p>
                    </div>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>


      {/* ─── YOUR GUIDES ────────────────────────────────────────── */}
      <section className="py-20 sm:py-28 bg-sand">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <FadeIn className="text-center mb-14 sm:mb-20">
            <p className="text-xs sm:text-sm uppercase tracking-[0.2em] text-amber-700 mb-4">
              Your Guides
            </p>
            <h2 className="text-3xl sm:text-5xl font-serif font-light text-stone-800">
              Authentic Sufi Practitioners
            </h2>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12">
            {/* Syed Danish Suhail */}
            <FadeIn>
              <div className="bg-white rounded-xl p-6 sm:p-8 shadow-sm border border-stone-100">
                <img
                  src="https://static.wixstatic.com/media/7b6fe5_13f0e720411844c1a14ae0da85757908~mv2.jpg/v1/fill/w_806,h_668,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/WhatsApp%20Image%202025-05-10%20at%2010_47_edited.jpg"
                  alt="Syed Danish Suhail"
                  className="w-28 h-28 sm:w-32 sm:h-32 rounded-full mx-auto mb-6 object-cover"
                />
                <h3 className="text-2xl font-serif text-stone-800 mb-1 text-center">
                  Syed Danish Suhail
                </h3>
                <p className="text-amber-700 text-sm text-center mb-5">
                  Founder, Beshno Retreats
                </p>
                <p className="text-stone-500 text-sm leading-relaxed mb-5">
                  Deeply rooted in the Chishti Sufi tradition, known for its emphasis on love,
                  tolerance, and service. Danish weaves ancient spiritual values into modern retreat
                  experiences, creating space for genuine transformation.
                </p>
                <blockquote className="border-l-2 border-amber-300 pl-4 text-stone-400 text-sm italic">
                  "My mission is to guide others on a journey that empowers them to increase their
                  empathy and understanding, both within themselves and in their relationships with others."
                </blockquote>
              </div>
            </FadeIn>

            {/* Abd al-Wadud Sabaté */}
            <FadeIn delay={0.15}>
              <div className="bg-white rounded-xl p-6 sm:p-8 shadow-sm border border-stone-100">
                <img
                  src="https://static.wixstatic.com/media/7b6fe5_08f770fea4e141a4aef2d5223208bd05~mv2.jpeg/v1/fill/w_726,h_602,al_c,q_85,enc_avif,quality_auto/WhatsApp%20Image%202025-06-06%20at%202_50_15%20PM.jpeg"
                  alt="Abd al-Wadud Sabaté"
                  className="w-28 h-28 sm:w-32 sm:h-32 rounded-full mx-auto mb-6 object-cover"
                />
                <h3 className="text-2xl font-serif text-stone-800 mb-1 text-center">
                  Abd al-Wadud Sabaté
                </h3>
                <p className="text-amber-700 text-sm text-center mb-5">
                  Naqshbandi Order &nbsp;&middot;&nbsp; Centre Sufi de Barcelona
                </p>
                <p className="text-stone-500 text-sm leading-relaxed mb-5">
                  Decades of experience guiding seekers on the authentic Sufi path. Known for his
                  gentle presence and profound wisdom in teaching spiritual awakening and self-knowledge.
                </p>
                <blockquote className="border-l-2 border-amber-300 pl-4 text-stone-400 text-sm italic">
                  "A rare opportunity to learn directly from a living practitioner rooted in tradition,
                  opening doors to spiritual connection for all who attend."
                </blockquote>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>


      {/* ─── WHAT'S INCLUDED ────────────────────────────────────── */}
      <section className="py-20 sm:py-28 bg-cream">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <FadeIn className="text-center mb-14 sm:mb-20">
            <p className="text-xs sm:text-sm uppercase tracking-[0.2em] text-amber-700 mb-4">
              What's Included
            </p>
            <h2 className="text-3xl sm:text-5xl font-serif font-light text-stone-800">
              Everything for your journey
            </h2>
          </FadeIn>

          <FadeIn delay={0.1}>
            <div className="bg-white rounded-xl p-6 sm:p-8 shadow-sm border border-stone-100">
              {includedItems.map((item, index) => (
                <AccordionItem
                  key={index}
                  item={item}
                  isOpen={openAccordion === index}
                  onToggle={() => setOpenAccordion(openAccordion === index ? null : index)}
                />
              ))}
            </div>
          </FadeIn>

          {/* Pricing block */}
          <FadeIn delay={0.2} className="mt-12">
            <div className="bg-stone-800 text-white rounded-xl p-8 sm:p-10 text-center">
              <div className="text-xs uppercase tracking-[0.2em] text-white/40 mb-3">Investment</div>
              <div className="text-3xl sm:text-4xl font-serif font-light mb-2">Pricing on Request</div>
              <div className="text-white/50 text-sm mb-5">
                Contact us via WhatsApp &nbsp;&middot;&nbsp; Payment plans offered
              </div>
              <div className="w-10 h-[1px] bg-white/15 mx-auto mb-5" />
              <p className="text-white/40 text-sm max-w-md mx-auto leading-relaxed">
                Spaces are limited for this intimate retreat. Reach out via WhatsApp to confirm
                availability and secure your place.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>


      {/* ─── TESTIMONIALS ───────────────────────────────────────── */}
      <section className="py-20 sm:py-28 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <FadeIn className="text-center mb-14 sm:mb-20">
            <p className="text-xs sm:text-sm uppercase tracking-[0.2em] text-amber-700 mb-4">Voices</p>
            <h2 className="text-3xl sm:text-5xl font-serif font-light text-stone-800">
              What past guests say
            </h2>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <TestimonialCard
              name="Khalid Maaroub"
              text="An excellent retreat to taste the flavor of Sufism in a beautiful place with wonderful company."
              delay={0}
            />
            <TestimonialCard
              name="Jannah"
              text="It was a blessing to be a part of this first Beshno retreat. To meet and start to know the beautiful, servant-hearted souls of the Beshno community, to begin learning of the Sufi way and share the week-end with them in this place of peace in nature. Danish exudes such humility, kindness and peace — in Rumi's words, he is one of those who obeys out of love."
              delay={0.1}
            />
            <TestimonialCard
              name="Sabrina"
              text="This retreat was a full heart connection opportunity as well as a beginning of something new in me. Come together as human beings, learning more about ourselves and other ways to live spirituality. I am really thankful for the generosity, the humility, the laughs, the humor, the openness, the deep listening and loving presence."
              delay={0.15}
            />
            <TestimonialCard
              name="Hannah"
              text="I found it to be a completely enlightening experience. It was a privilege to learn about the essence of Sufism and be welcomed into such a beautiful universe with open arms. I left the retreat more connected to my true nature and inspired to bring this connection into my daily life."
              delay={0.2}
            />
          </div>
        </div>
      </section>


      {/* ─── WEEKEND RETREAT CROSS-PROMO ────────────────────────── */}
      <section className="py-20 sm:py-28 bg-stone-800 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-14 items-center">
            <FadeIn>
              <div className="relative rounded-xl overflow-hidden">
                <img
                  src="https://static.wixstatic.com/media/11062b_0bf57831f109491aba0974490795c48d~mv2.jpeg/v1/fill/w_1074,h_1058,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/11062b_0bf57831f109491aba0974490795c48d~mv2.jpeg"
                  alt="Weekend retreat setting"
                  className="w-full aspect-[4/5] object-cover"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-stone-900/80 to-transparent p-6">
                  <div className="text-white/80 text-sm">April &amp; May 2026</div>
                  <div className="text-white text-lg font-serif">A Gentle Introduction</div>
                </div>
              </div>
            </FadeIn>

            <FadeIn delay={0.15}>
              <p className="text-xs sm:text-sm uppercase tracking-[0.2em] text-white/40 mb-4">
                Not Ready for 7 Days?
              </p>
              <h2 className="text-3xl sm:text-4xl font-serif font-light mb-6 leading-tight">
                The Sufi Way &mdash; Weekend Retreat
              </h2>
              <p className="text-white/60 leading-relaxed mb-8">
                Begin your journey with a 2-night weekend retreat in the Barcelona countryside.
                A gentle, immersive introduction to Sufi wisdom — the perfect first step before
                committing to the full 7-day experience.
              </p>

              <div className="space-y-3 mb-8">
                {[
                  "Muraqaba meditation & guided practices",
                  "Live Sufi music & hadra circle",
                  "Sufi yoga & nature trek",
                  "All meals & 2-night accommodation",
                  "Authentic practitioners, small group"
                ].map((feature, index) => (
                  <div key={index} className="flex items-center gap-3 text-white/70 text-sm">
                    <div className="w-1 h-1 bg-white/40 rounded-full flex-shrink-0" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>

              <a
                href="/"
                className="inline-flex items-center gap-2 text-white border border-white/30 px-6 py-3 rounded-full text-sm hover:bg-white/10 transition-all min-h-[44px]"
              >
                Explore Weekend Retreats <ArrowUpRight className="w-4 h-4" />
              </a>
            </FadeIn>
          </div>
        </div>
      </section>


      {/* ─── FINAL CTA ──────────────────────────────────────────── */}
      <section className="py-24 sm:py-32 bg-stone-900 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <FadeIn>
            <p className="text-[11px] sm:text-xs uppercase tracking-[0.3em] text-amber-400/70 mb-8">
              Whispers of the Heart &nbsp;&middot;&nbsp; Barcelona 2027
            </p>
            <h2 className="text-3xl sm:text-5xl md:text-6xl font-serif font-light mb-6 leading-tight">
              Seven days.<br />A different life.
            </h2>
            <p className="text-xl sm:text-2xl font-serif font-light text-white/55 mb-12 italic">
              Come with an open heart.
            </p>

            <div className="mb-10">
              <div className="text-white/35 text-xs uppercase tracking-[0.15em] mb-2">Retreat Dates</div>
              <div className="text-lg sm:text-xl font-serif mb-1">March 22–28, 2027</div>
              <div className="text-white/40 text-sm">Alaya Retreat Center, Barcelona</div>
            </div>

            <div className="w-12 h-[1px] bg-amber-500/40 mx-auto mb-10" />

            <div className="space-y-4">
              <a
                href="https://wa.me/34617159090"
                className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-stone-900 px-8 py-3.5 sm:px-10 sm:py-4 rounded-full text-sm sm:text-base font-medium transition-all shadow-lg"
              >
                Reserve Your Place via WhatsApp
                <ArrowUpRight className="w-4 h-4" />
              </a>
              <p className="text-white/35 text-sm">
                or email{" "}
                <a
                  href="mailto:beshnoretreats@gmail.com"
                  className="text-white/55 hover:text-white/80 underline transition-colors"
                >
                  beshnoretreats@gmail.com
                </a>
              </p>
            </div>
          </FadeIn>
        </div>
      </section>


      {/* ─── FOOTER ─────────────────────────────────────────────── */}
      <footer className="bg-stone-950 text-white/40 py-10 sm:py-14">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <img
            src="/beshnologo.png"
            alt="Beshno Retreats"
            className="h-8 sm:h-10 w-auto mx-auto mb-6 brightness-0 invert opacity-40"
          />

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 mb-6 text-sm">
            <a
              href="mailto:beshnoretreats@gmail.com"
              className="flex items-center gap-2 hover:text-white/65 transition-colors min-h-[44px]"
            >
              <Mail className="w-4 h-4" />
              beshnoretreats@gmail.com
            </a>
            <a
              href="tel:+34617159090"
              className="flex items-center gap-2 hover:text-white/65 transition-colors min-h-[44px]"
            >
              <Phone className="w-4 h-4" />
              +34 617 159 090
            </a>
          </div>

          <div className="border-t border-white/[0.07] pt-6">
            <p className="text-xs">&copy; 2027 Beshno Retreats &nbsp;&middot;&nbsp; Barcelona, Spain</p>
          </div>
        </div>
      </footer>

    </div>
  );
};

export default MarchRetreat;
